import omit from 'lodash';
import React, { useRef } from 'react';

import classnames from 'classnames';
import Creatable from 'react-select/creatable';
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  OnChangeValue,
  PlaceholderProps,
  SelectInstance,
  Props as SelectProps,
  StylesConfig,
  ValueContainerProps,
  components,
} from 'react-select';

interface MaterialDropdownOption {
  label?: React.ReactNode;
  value: string | number | null;
}

interface MaterialDropdownProps extends Omit<
  SelectProps<MaterialDropdownOption, boolean>,
  | 'isMulti'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'options'
  | 'styles'
  | 'isDisabled'
  | 'isLoading'
  | 'name'
  | 'className'
  | 'components'
  | 'placeholder'
  | 'formatCreateLabel'
  | 'onInputChange'
  | 'onMenuClose'
  | 'closeMenuOnSelect'
> {
  isPlain?: boolean;
  isSmall?: boolean;
  isMedium?: boolean;
  isBig?: boolean;
  isSearchable?: boolean;
  customClassName?: string;
  defaultValue?: MaterialDropdownOption | MaterialDropdownOption[] | null;
  isLoading?: boolean;
  value?: MaterialDropdownOption | MaterialDropdownOption[] | null;
  options?: MaterialDropdownOption[];
  name?: string;
  onDropDownChange?: (value: OnChangeValue<MaterialDropdownOption, boolean>) => void;
  onInputChange?: (inputValue: string) => void;
  autoResize?: boolean;
  isMulti?: boolean;
  isCreatable?: boolean;
  placeholder?: string;
  customStyles?: StylesConfig<MaterialDropdownOption, boolean, GroupBase<MaterialDropdownOption>>;
  onMenuClose?: () => void;
  disabled?: boolean;
  isStatusFilter?: boolean;
  closeMenuOnSelect?: boolean;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
}

const MaterialDropdown: React.FC<MaterialDropdownProps> = (props) => {
  const {
    isPlain,
    isSmall,
    isMedium,
    isBig,
    customClassName,
    defaultValue,
    isLoading,
    value,
    name,
    onDropDownChange,
    onInputChange,
    autoResize = false,
    isMulti = false,
    isCreatable = false,
    placeholder,
    customStyles,
    onMenuClose,
    disabled,
    isStatusFilter,
    closeMenuOnSelect,
  } = props;

  const selectClass = classnames(
    'lf-select',
    {
      'lf-select--plain': isPlain,
      'lf-select--small': isSmall,
      'lf-select--medium': isMedium,
      'lf-select--big': isBig,
      'lf-select--status': isStatusFilter,
    },
    customClassName
  );

  const { ValueContainer, Placeholder } = components;

  const handleFormatCreateLabel = (inputValue: string) => {
    if (props.formatCreateLabel) return props.formatCreateLabel(inputValue);

    return inputValue;
  };

  const SelectComponent = isCreatable ? Creatable : Select;

  const CustomPlaceholderContainer = (
    selectProps: ValueContainerProps<MaterialDropdownOption, boolean, GroupBase<MaterialDropdownOption>>
  ) => {
    const { children } = selectProps;
    return (
      <>
        <Placeholder
          {...(selectProps as PlaceholderProps<MaterialDropdownOption, boolean, GroupBase<MaterialDropdownOption>>)}
        >
          {selectProps.selectProps.placeholder}
        </Placeholder>
        <ValueContainer {...selectProps}>
          {React.Children.map(children, (child) =>
            React.isValidElement(child) && child.type !== Placeholder ? (
              <div
                title={
                  typeof (child as React.ReactElement<{ children?: React.ReactNode }>).props.children === 'string'
                    ? ((child as React.ReactElement<{ children?: React.ReactNode }>).props.children as string)
                    : undefined
                }
              >
                {child}
              </div>
            ) : null
          )}
        </ValueContainer>
      </>
    );
  };

  const ref = useRef<SelectInstance<MaterialDropdownOption, boolean, GroupBase<MaterialDropdownOption>> | null>(null);
  const styles: StylesConfig<MaterialDropdownOption, boolean, GroupBase<MaterialDropdownOption>> = {
    singleValue: (style) => (autoResize ? omit(style, ['maxWidth']) : { ...style }) as CSSObjectWithLabel,
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      marginTop: 2,
      minWidth: '80px',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      width: '100%',
    }),
    valueContainer: (style) => ({
      ...style,
      top: '8px',
      marginLeft: '2px',
    }),
    placeholder: (style) => ({
      ...style,
      marginTop: '12px',
      top: '-20px',
      padding: '8px',
      fontWeight: 'normal',
      textAlign: 'left',
      position: 'absolute',
    }),
    option: (style, { isSelected }) => ({
      ...style,
      color: isSelected ? '#ffffff' : '#999999',
    }),
    control: (style, { isFocused }) => ({
      ...style,
      padding: '8px 0px',
      background: isFocused && 'white',
    }),
    ...customStyles,
  };

  return (
    <div className="custom-dropdown">
      <SelectComponent
        ref={ref}
        name={name ? name : ''}
        placeholder={placeholder || 'Select'}
        isLoading={isLoading}
        defaultValue={defaultValue}
        value={value}
        isDisabled={disabled}
        onChange={onDropDownChange}
        classNamePrefix="material-dropdown-select"
        menuPortalTarget={document.body}
        styles={styles}
        menuPosition={'absolute'}
        closeMenuOnSelect={closeMenuOnSelect}
        menuPlacement={'auto'}
        className={selectClass}
        onInputChange={onInputChange}
        isMulti={isMulti}
        components={{ ValueContainer: CustomPlaceholderContainer }}
        formatCreateLabel={handleFormatCreateLabel}
        autoResize={autoResize}
        onMenuClose={() => {
          ref.current?.blur();
          onMenuClose?.();
        }}
        {...props}
      />
    </div>
  );
};

export default MaterialDropdown;
